import { useCallback, useEffect, useRef, useState } from "react";

interface UseIdleTimerOptions {
	timeout: number;
	onIdle: () => void;
	events?: string[];
	enabled?: boolean;
}

const DEFAULT_EVENTS = ["mousedown", "keypress", "scroll", "touchstart"];

export function useIdleTimer({
	timeout,
	onIdle,
	events = DEFAULT_EVENTS,
	enabled = true,
}: UseIdleTimerOptions) {
	const [isIdle, setIsIdle] = useState(false);
	const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
	const onIdleRef = useRef(onIdle);
	const wasIdleRef = useRef(false);

	// Update the onIdle callback ref when the onIdle callback changes
	useEffect(() => {
		onIdleRef.current = onIdle;
	}, [onIdle]);

	const clearTimer = useCallback(() => {
		if (timeoutIdRef.current) {
			clearTimeout(timeoutIdRef.current);
			timeoutIdRef.current = null;
		}
	}, []);

	const startTimer = useCallback(() => {
		if (!enabled) return;
		clearTimer();
		timeoutIdRef.current = setTimeout(() => {
			setIsIdle(true);
			wasIdleRef.current = true;
		}, timeout);
	}, [timeout, enabled, clearTimer]);

	const resetTimer = useCallback(() => {
		if (!enabled) return;

		// Only fire callback if user was previously idle and is now active again
		if (wasIdleRef.current) {
			onIdleRef.current();
			wasIdleRef.current = false;
		}
		setIsIdle(false);
		startTimer();
	}, [enabled, startTimer]);

	// Set up event listeners and timer
	useEffect(() => {
		if (!enabled) {
			clearTimer();
			return;
		}
		startTimer();
		for (const event of events) {
			window.addEventListener(event, resetTimer);
		}
		return () => {
			clearTimer();
			for (const event of events) {
				window.removeEventListener(event, resetTimer);
			}
		};
	}, [startTimer, resetTimer, clearTimer, events, enabled]);

	return {
		isIdle,
		resetTimer,
	};
}
